import {NaoAutorizadoComponent} from '~pages/nao-autorizado/nao-autorizado.component';
import {NgModule} from '@angular/core';
import {PreloadAllModules, Route, RouterModule} from '@angular/router';
import {LogadoActiveGuard} from '~core/security/guards/logado-active.guard';
import {PrintLayoutComponent} from '~shared/refatorar/print-layout/print-layout.component';
import {InvoiceComponent} from '~shared/refatorar/invoice/invoice.component';
import {Paths} from '~core/security/auth.roles';
import {singleSpaRoutingOptions} from 'src/single-spa/single-spa-routing';
import {APP_BASE_HREF} from '@angular/common';
import {environment} from '~env/environment';

const routeLogado = <T extends import('@angular/core').Type<any> | import('@angular/router').Routes | import('@angular/core').NgModuleFactory<any>>(
  path: Paths,
  load: () => Promise<T>,
  pathSufix?: string,
  pathMatch?: 'full' | 'prefix'
): Route =>
  ({path: path.toString().concat(pathSufix || ''), loadChildren: load, canActivate: [LogadoActiveGuard], ...(pathMatch && {pathMatch})});

const printChildren = [{path: 'invoice/:invoiceParams', component: InvoiceComponent}];

const loadLogin = () => import('./paginas/login/login.module').then(m => m.LoginModule);
const loadGrid = () => import('./paginas/grid/grid.module').then(m => m.GridModule);
const loadMapa = () => import('./paginas/mapa/mapa.module').then(m => m.MapaModule);
const loadAdminVisitas = () => import('./paginas/administrar-visitas/administrar-visitas.module').then(m => m.AdministrarVisitasModule);
const loadReprogramacao = () => import('./paginas/reprogramacao/reprogramacao.module').then(m => m.ReprogramacaoModule);
const loadConfig = () => import('./paginas/config/configuracoes.module').then(m => m.ConfiguracoesModule);
const loadPainel = () => import('./paginas/config/painel/painel.module').then(m => m.PainelModule);
const loadSeguranca = () => import('./paginas/seguranca/seguranca.module').then(m => m.SegurancaModule);
const loadImportacao = () => import('./paginas/importacao/importacao.module').then(m => m.ImportacaoModule);
const loadConfigGrupoUsuario = () => import('./paginas/config-grupo-usuario/config-grupo-usuario.module').then(m => m.ConfigGrupoUsuarioModule);
const loadFinanceiro = () => import('./paginas/financeiro/financeiro.module').then(m => m.FinanceiroModule);
const loadDashboard = () => import('./paginas/dashboard/dashboard.module').then(m => m.DashboardModule);
const loadBatePapo = () => import('./paginas/bate-papo/bate-papo.module').then(m => m.BatePapoModule);
const loadAlertas = () => import('./paginas/administrar-alertas/administrar-alertas.module').then(m => m.AdministrarAlertasModule);
const loadRelatorios = () => import('./paginas/relatorios/relatorios.module').then(m => m.RelatoriosModule);
const loadPortalCliente = () => import('./paginas/portal-cliente/portal-cliente.module').then(m => m.PortalClienteModule);
const loadTalks = () => import('./paginas/chat/chat.module').then(m => m.ChatModule);

const loadChildrenVerServicos = () => import('./paginas/visualizar-servicos/visualizar-servicos.module').then(m => m.VisualizarServicosModule);
const loadSistema = () => import('./paginas/config/sistema/sistema.module').then(m => m.SistemaModule);
const loadIncidentes = () => import('./paginas/config/incidentes/incidentes.module').then(m => m.IncidentesModule);
const loadIndicadores = () => import('./paginas/config/indicadores/indicadores.module').then(m => m.IndicadoresModule);
const loadLayoutImportacao = () => import('./paginas/config/layout-importacao/layout-importacao.module').then(m => m.LayoutImportacaoModule);
const loadRelatorio = () => import('./paginas/config/relatorio/relatorio.module').then(m => m.RelatorioModule);
const loadDespesas = () => import('./paginas/config/despesas/despesas.module').then(m => m.DespesasModule);
const loadIntegracao = () => import('./paginas/config/integracao/integracao.module').then(m => m.IntegracaoModule);
const loadTratativa = () => import('./paginas/config/tratativa/tratativa.module').then(m => m.TratativaModule);

@NgModule({
  imports: [RouterModule.forRoot([
    routeLogado(Paths.LOGIN, loadLogin),
    routeLogado(Paths.GRID, loadGrid),
    routeLogado(Paths.MAPA, loadMapa),
    routeLogado(Paths.ADMIN_VISITAS, loadAdminVisitas),
    routeLogado(Paths.REPROGRAMACAO, loadReprogramacao),
    // Rotas específicas de configurações devem vir ANTES da rota base
    {path: 'configuracoes/sistema', loadChildren: loadSistema, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/incidentes', loadChildren: loadIncidentes, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/indicadores', loadChildren: loadIndicadores, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/importacao', loadChildren: loadLayoutImportacao, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/importacao/:mode', loadChildren: loadLayoutImportacao, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/importacao/:mode/:id', loadChildren: loadLayoutImportacao, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/relatorio', loadChildren: loadRelatorio, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/despesas', loadChildren: loadDespesas, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/integracao', loadChildren: loadIntegracao, canActivate: [LogadoActiveGuard]},
    {path: 'configuracoes/tratativa', loadChildren: loadTratativa, canActivate: [LogadoActiveGuard]},
    // Painel (Dashboard planejado x realizado) — somente Admin, via prefixo /configuracoes
    {path: 'configuracoes/painel', loadChildren: loadPainel, canActivate: [LogadoActiveGuard]},
    // Rota base de configurações (apenas para /configuracoes exato, não subpaths)
    {path: 'configuracoes', loadChildren: loadConfig, canActivate: [LogadoActiveGuard], pathMatch: 'full'},
    // Rotas genéricas com parâmetros (para tabs do ConfiguracoesComponent)
    routeLogado(Paths.CONFIG, loadConfig, '/:tabName'),
    routeLogado(Paths.CONFIG, loadConfig, '/:tabName/:mode'),
    routeLogado(Paths.CONFIG, loadConfig, '/:tabName/:mode/:id'),
    {path: 'sistema', redirectTo: 'configuracoes/sistema', pathMatch: 'full'},
    routeLogado(Paths.SEGURANCA, loadSeguranca),
    routeLogado(Paths.IMPORTACAO, loadImportacao),
    routeLogado(Paths.GRUPOUSUARIO, loadConfigGrupoUsuario),
    routeLogado(Paths.GRUPOUSUARIO, loadConfigGrupoUsuario, '/:tabName'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visitas/servicos'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visitas/:visitas/servicos'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visita/:codVisita/servicos'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visitas/remover'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visitas/:visitas/remover'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visitas/resetStatus'),
    routeLogado(Paths.ROTA, loadChildrenVerServicos, '/:rota/visitas/:visitas/resetStatus'),
    routeLogado(Paths.FINANCEIRO, loadFinanceiro),
    routeLogado(Paths.DASHBOARD, loadDashboard),
    routeLogado(Paths.CHAT, loadBatePapo),
    routeLogado(Paths.CHAT, loadBatePapo, '/:roomId'),
    routeLogado(Paths.TALKS, loadTalks),
    routeLogado(Paths.ALERTAS_INCIDENTES, loadAlertas),
    routeLogado(Paths.ALERTAS_INCIDENTES, loadAlertas, '/:rota'),
    routeLogado(Paths.RELATORIOS, loadRelatorios),
    {path: Paths.PRINT, outlet: Paths.PRINT, component: PrintLayoutComponent, children: printChildren},
    {path: Paths.NAO_AUTORIZADO, component: NaoAutorizadoComponent},
    {path: Paths.PORTAL_CLIENTE, loadChildren: loadPortalCliente },

    {path: '**', redirectTo: Paths.GRID, pathMatch: 'full'},
  ], {
    ...singleSpaRoutingOptions(),
    preloadingStrategy: PreloadAllModules,
  })],
  exports: [RouterModule],
  providers: [{provide: APP_BASE_HREF, useValue: environment.appBaseHref ?? '/'}],
})
export class AppRoutingModule {
}
